import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Trash2, ExternalLink, Image as ImageIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import MediaPicker from "@/components/MediaPicker";

const REGION_OPTIONS = ["Southeast Asia", "East Asia", "Beyond"];

function Text({ label, value, onChange, testid, help }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="input-line mt-3" />
      {help && <p className="mt-2 text-[12px] text-ink/50 italic">{help}</p>}
    </div>
  );
}
function Area({ label, value, onChange, testid, rows = 3 }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={rows} className="input-line mt-3 resize-y" />
    </div>
  );
}

function ImagePick({ label, value, onChange, testid, help }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <div className="mt-3 flex items-start gap-4">
        <div className="w-32 h-32 bg-mist/40 border border-mist flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={22} className="text-ink/30" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-3">
            <button type="button" onClick={() => setOpen(true)} data-testid={`${testid}-pick`} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper">Choose from library</button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="text-[12px] tracking-[0.14em] uppercase text-ink/50 hover:text-copper inline-flex items-center gap-1">
                <X size={12} /> Clear
              </button>
            )}
          </div>
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste an image URL or pick from library" data-testid={testid} className="input-line text-[13px]" />
          {help && <p className="text-[12px] text-ink/50 italic">{help}</p>}
        </div>
      </div>
      <MediaPicker open={open} onClose={() => setOpen(false)} onPick={onChange} />
    </div>
  );
}

function ListEditor({ label, items, onChange, placeholder, testid }) {
  const add = () => onChange([...(items || []), ""]);
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <div className="mt-3 space-y-2">
        {(items || []).map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              data-testid={`${testid}-${i}`}
              value={v}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                onChange(next);
              }}
              placeholder={placeholder}
              className="input-line flex-1"
            />
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-ink/55 hover:text-copper"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid={`${testid}-add`} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper">+ Add</button>
      </div>
    </div>
  );
}

function RegionListEditor({ items, onChange }) {
  const add = () => onChange([...(items || []), { name: "", note: "" }]);
  return (
    <div>
      <label className="eyebrow text-ink/55">Regions we cover</label>
      <div className="mt-3 space-y-3">
        {(items || []).map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input data-testid={`dest-region-name-${i}`} value={r.name || ""} onChange={(e) => { const n = [...items]; n[i] = { ...r, name: e.target.value }; onChange(n); }} placeholder="Region name" className="input-line col-span-4" />
            <input data-testid={`dest-region-note-${i}`} value={r.note || ""} onChange={(e) => { const n = [...items]; n[i] = { ...r, note: e.target.value }; onChange(n); }} placeholder="Short note (e.g. Old Quarter, Halong)" className="input-line col-span-7" />
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-ink/55 hover:text-copper col-span-1"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper" data-testid="dest-region-add">+ Add region</button>
      </div>
    </div>
  );
}

const EMPTY = {
  slug: "", name: "", region: "Southeast Asia", order: 0, status: "draft",
  card_title: "", card_subtitle: "", hero_title: "", hero_subtitle: "",
  intro: "", tagline: "", summary: "", description: "",
  regions: [], experiences: [],
  best_for: "", suggested_duration: "", best_time_to_travel: "",
  hero_image: "", featured_image: "",
  seo: { title: "", description: "", og_title: "", og_description: "" },
};

export default function DestinationEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = slug === "new";
  const [form, setForm] = useState(isNew ? { ...EMPTY } : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/destinations/${slug}`).then(r => setForm({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } }))
        .catch(() => { toast.error("Not found"); navigate("/studio/destinations", { replace: true }); });
    }
  }, [slug, isNew, navigate]);

  if (!form) return <p className="text-ink/50 text-sm">Loading…</p>;

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const setSeo = (k) => (v) => setForm({ ...form, seo: { ...form.seo, [k]: v } });

  const save = async (e) => {
    e?.preventDefault();
    if (!form.name?.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      let r;
      if (isNew) r = await api.post("/admin/destinations", form);
      else r = await api.put(`/admin/destinations/${slug}`, form);
      toast.success("Saved.");
      if (isNew || r.data.slug !== slug) navigate(`/studio/destinations/${r.data.slug}`, { replace: true });
      else setForm({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/destinations/${slug}`);
      toast.success("Deleted.");
      navigate("/studio/destinations", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid={`destination-edit-${slug}`}>
      <Link to="/studio/destinations" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-ink/60 hover:text-copper">
        <ChevronLeft size={14} /> Back to destinations
      </Link>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-serif text-4xl text-jade font-light">{isNew ? "New destination" : form.name}</h1>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && form.status === "published" && (
            <a target="_blank" rel="noopener noreferrer" href={`/destinations/${form.slug}`} className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2">
              <ExternalLink size={14} /> View
            </a>
          )}
          <select data-testid="dest-status" value={form.status || "draft"} onChange={(e) => set("status")(e.target.value)} className="border border-mist px-3 py-2 text-[13px] bg-ivory">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      <form onSubmit={save} className="mt-12 space-y-14">
        <section className="space-y-6">
          <p className="eyebrow text-copper">Basics</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Text label="Destination name" value={form.name} onChange={set("name")} testid="dest-name" />
            <Text label="Slug (URL)" value={form.slug} onChange={set("slug")} testid="dest-slug" help="Leave blank to auto-generate from name on first save." />
            <div>
              <label className="eyebrow text-ink/55">Region</label>
              <select data-testid="dest-region" value={form.region || ""} onChange={(e) => set("region")(e.target.value)} className="input-line mt-3 bg-ivory">
                <option value="">—</option>
                {REGION_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow text-ink/55">Display order</label>
              <input type="number" data-testid="dest-order" value={form.order ?? 0} onChange={(e) => set("order")(parseInt(e.target.value, 10) || 0)} className="input-line mt-3" />
            </div>
          </div>
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Card &amp; Hero</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Text label="Card title (defaults to name)" value={form.card_title} onChange={set("card_title")} testid="dest-card-title" />
            <Text label="Card subtitle (defaults to tagline)" value={form.card_subtitle} onChange={set("card_subtitle")} testid="dest-card-subtitle" />
            <Text label="Hero title (defaults to name)" value={form.hero_title} onChange={set("hero_title")} testid="dest-hero-title" />
            <Text label="Hero subtitle (defaults to tagline)" value={form.hero_subtitle} onChange={set("hero_subtitle")} testid="dest-hero-subtitle" />
            <Text label="Tagline (used on card + hero subtitle as fallback)" value={form.tagline} onChange={set("tagline")} testid="dest-tagline" />
          </div>
          <ImagePick label="Hero image" value={form.hero_image} onChange={set("hero_image")} testid="dest-hero-image" help="Used on the destination detail page." />
          <ImagePick label="Featured / card image" value={form.featured_image} onChange={set("featured_image")} testid="dest-featured-image" help="Falls back to the hero image if blank." />
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Story</p>
          <Area label="Intro paragraph" value={form.intro} onChange={set("intro")} testid="dest-intro" rows={3} />
          <Area label="Short summary (used on cards)" value={form.summary} onChange={set("summary")} testid="dest-summary" rows={2} />
          <Area label="Main description" value={form.description} onChange={set("description")} testid="dest-description" rows={6} />
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Details</p>
          <RegionListEditor items={form.regions} onChange={set("regions")} />
          <ListEditor label="Signature experiences" items={form.experiences} onChange={set("experiences")} placeholder="e.g. Private dawn cruise on Bai Tu Long" testid="dest-experience" />
          <div className="grid md:grid-cols-3 gap-6">
            <Text label="Best for" value={form.best_for} onChange={set("best_for")} testid="dest-best-for" help="e.g. Honeymoons, culture-led couples" />
            <Text label="Suggested duration" value={form.suggested_duration} onChange={set("suggested_duration")} testid="dest-duration" help="e.g. 10 to 14 days" />
            <Text label="Best time to travel" value={form.best_time_to_travel} onChange={set("best_time_to_travel")} testid="dest-best-time" help="e.g. October to April" />
          </div>
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">SEO &amp; social</p>
          <Text label="Page title" value={form.seo?.title} onChange={setSeo("title")} testid="dest-seo-title" />
          <Area label="Meta description" value={form.seo?.description} onChange={setSeo("description")} testid="dest-seo-desc" rows={2} />
          <Text label="Open Graph title" value={form.seo?.og_title} onChange={setSeo("og_title")} testid="dest-seo-og-title" />
          <Area label="Open Graph description" value={form.seo?.og_description} onChange={setSeo("og_description")} testid="dest-seo-og-desc" rows={2} />
        </section>

        <div className="pt-4 flex justify-between gap-3">
          {!isNew && (
            <button type="button" onClick={remove} data-testid="dest-delete" className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2">
              <Trash2 size={14} /> Delete destination
            </button>
          )}
          <div className="ml-auto flex gap-3">
            <Link to="/studio/destinations" className="btn-secondary">Cancel</Link>
            <button data-testid="dest-save" disabled={saving} type="submit" className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
